// @givmo/fundraisers-module — the Campaign tab's three cards for nested campaigns (FCM-WEB25; frames 11 to
// 11c): the fundraising deadline (FCM-BE35) and the time zone it is set in (FCM-BE61, P9), who picks the
// charities and the optional list (FCM-BE37; Q1 and Q8), and how the campaign's card looks (decision 3;
// FCM-BE56, FCM-BE66). What each card offers is the server's data — the settings read's options, the list
// that binds it, the look its shape gives — never a branch on the organization's class (R5).
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  sdk,
  type P2pConfigOut,
  type P2pConfigRequest,
  type P2pRecipientListIn,
} from '@givmo/api-client';
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  DatePicker,
  Icon,
  RadioTile,
  RadioTileGroup,
  toast,
} from '@givmo/design-system';
import { Lock, Plus, X } from 'lucide-react';
import { orgSettingsHref } from './fundraisers-config';
import { useFundraisersModule } from './fundraisers-context';
import { goalPercentBarWidth } from './fundraisers-decode';
import { displayP2pMoney } from './p2p-decode';
import { LAYERS_COPY as C, SERVER_WORDS, series } from './layers-copy';
import {
  daysLeft,
  deadlineBody,
  deadlineDay,
  formatEnd,
  formatEndDay,
  deadlineZone,
  layerRefusal,
  sameDay,
  storedLayerName,
  zoneName,
} from './layers-decode';
import { publicBoardsOptions, publicCampaignOptions, scheduleOptions } from './layers-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import { ModuleLink } from './fundraisers-state';
import { keepDayTogether } from './season-setup-decode';
import { PickerResults, RecipientSearch } from './recipient-picker';
import { useRecipientNames } from './recipient-names';
import type { RecipientItem } from './recipients-decode';
import { useLivePage } from './use-live-page';

type Saved = () => Promise<unknown>;

function saveFailed(live: () => boolean, error: unknown) {
  if (live()) toast.error(layerRefusal(error).message);
}

// ── The fundraising deadline ─────────────────────────────────────────────────────────────────────

/**
 * A campaign's own deadline, and its end: the earliest of that deadline and every campaign above's. The
 * deadline is a day, ending at 11:59 PM that day in the time zone it is set in (decision 7, P9), saved with
 * that zone and named on the card, so every viewer reads the same moment.
 */
export function DeadlineCard({
  campaignId,
  config,
  editable,
  ended = false,
  onSaved,
}: {
  campaignId: string;
  config: P2pConfigOut;
  /** Moves the deadline: the moderation rank, on a campaign that hasn't ended (or ended on this deadline). */
  editable: boolean;
  /** The campaign has ended: its end is the record, and the campaign above's limit no longer a choice. */
  ended?: boolean;
  onSaved: Saved;
}) {
  const live = useLivePage();
  const module = useFundraisersModule();
  // A new deadline ends in the organization's own zone, else the reader's (`deadlineZone`).
  const newZone = deadlineZone(module.orgTimeZone);
  const settingsHref = orgSettingsHref(module);
  const own = config.deadline_at ?? null;
  const ownDay = useMemo(
    () => (own ? deadlineDay(own, config.deadline_time_zone) : undefined),
    [own, config.deadline_time_zone],
  );
  const [day, setDay] = useState<Date | undefined>(ownDay);
  useEffect(() => setDay(ownDay), [ownDay]);
  const changed = !sameDay(day, ownDay);
  const save = useMutation({
    mutationFn: () =>
      sdk.campaignsUpdateCampaign({
        path: { fundraiser_id: campaignId },
        body: deadlineBody(day, newZone),
        throwOnError: true,
      }),
    onSuccess: async () => {
      await onSaved();
      if (live()) toast.success(C.deadlineSaved);
    },
    onError: (error) => saveFailed(live, error),
  });
  const endsWith = config.ends_with ?? null;
  const status = config.ends_at
    ? endsWith
      ? C.endsWith(formatEnd(config.ends_at, config.ends_time_zone), endsWith.campaign_name)
      : C.endsOn(formatEnd(config.ends_at, config.ends_time_zone))
    : C.noDeadline;
  return (
    <Card flush testId="campaign-deadline-card">
      <CardHeader title={C.deadlineTitle} subtitle={C.deadlineSubtitle} />
      <CardBody className="flex flex-col gap-16">
        {editable && (
          <div className="flex flex-col gap-12 sm:flex-row sm:items-end">
            <DatePicker
              label={C.deadlineLabel}
              placeholder={C.deadlinePlaceholder}
              value={day}
              onValueChange={setDay}
              disabled={save.isPending}
              className="sm:w-[280px]"
              testId="campaign-deadline-date"
            />
            <Button
              appearance="saas"
              tone="secondary"
              disabled={!changed || save.isPending}
              loading={save.isPending}
              onClick={() => save.mutate()}
              testId="campaign-deadline-save"
            >
              {C.saveDeadline}
            </Button>
          </div>
        )}
        <div className="flex flex-col gap-4" data-testid="campaign-deadline-status">
          <p className="text-s14 font-w500 text-text-strong">{status}</p>
          {endsWith && !ended && (
            <p className="text-s13 leading-h15 text-text-secondary">
              {C.earlierOnly(endsWith.campaign_name)}
            </p>
          )}
          {/* P9: whose time zone — the one a saved deadline is set in, named; a new one is set in the
              organization's own, else the reader's, with the way to change it. */}
          {editable && !endsWith && (
            <p
              className="text-s12 leading-h15 text-text-secondary"
              data-testid="campaign-deadline-zone"
            >
              {own && !changed
                ? C.deadlineZone(zoneName(config.deadline_time_zone))
                : C.deadlineZone(
                    zoneName(newZone),
                    module.orgTimeZone && newZone === module.orgTimeZone ? 'org' : 'reader',
                  )}
              {settingsHref !== null && (
                <>
                  {' '}
                  <ModuleLink
                    href={settingsHref}
                    className="font-w600 text-brand-700 hover:underline"
                    testId="campaign-deadline-zone-settings"
                  >
                    {C.changeZone}
                  </ModuleLink>
                </>
              )}
            </p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ── Who picks the charities ──────────────────────────────────────────────────────────────────────

/** Recipients as chips, each removable while `onRemove` is given (the list editor, the add dialog). */
export function Chips({
  items,
  testId,
  onRemove,
}: {
  items: readonly RecipientItem[];
  testId: string;
  onRemove?: (item: RecipientItem) => void;
}) {
  return (
    <ul className="flex flex-wrap gap-8" data-testid={testId}>
      {items.map((item) => (
        <li
          key={`${item.kind}:${item.id}`}
          className={`flex max-w-full items-center gap-8 rounded-full border border-border-subtle bg-surface py-4 pl-4 ${onRemove ? 'pr-8' : 'pr-12'}`}
        >
          <Avatar src={item.imageUrl ?? undefined} name={item.name} size="xs" />
          {/* A name wraps, never cut off (tester-5's item 12). */}
          <span className="min-w-0 break-words text-s13 font-w500 text-text-strong">
            {item.name}
          </span>
          {onRemove && (
            <button
              type="button"
              aria-label={C.removeFromList(item.name)}
              onClick={() => onRemove(item)}
              className="relative rounded-full p-2 text-text-tertiary outline-none transition-colors after:absolute after:-inset-8 hover:text-danger-600 focus-visible:text-danger-600"
            >
              <Icon icon={X} size={14} aria-hidden />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A recipient's identity across kinds: "charity:{id}", "etf:{id}", "collection:{id}". */
export const recipientKey = (item: { kind: string; id: string }) => `${item.kind}:${item.id}`;
const key = recipientKey;

export function idsOf(items: readonly RecipientItem[]): P2pRecipientListIn {
  return {
    charity_ids: items.filter(({ kind }) => kind === 'charity').map(({ id }) => id),
    etf_ids: items.filter(({ kind }) => kind === 'etf').map(({ id }) => id),
    collection_ids: items.filter(({ kind }) => kind === 'collection').map(({ id }) => id),
  };
}

/**
 * Who picks the charities (Q1): the campaign, or each page and layer, optionally from a list. The choice
 * binds everything under the campaign, and locks once it has a supporter, a gift or a campaign above or
 * below it. The settings read names no lock, so the card reads the same facts the server's lock does.
 */
export function WhoPicksCard({
  campaignId,
  campaignName,
  config,
  editable,
  locked,
  lockedByAbove,
  draft,
  ownCharities,
  bound,
  onSaved,
}: {
  campaignId: string;
  campaignName: string;
  config: P2pConfigOut;
  /** The moderation rank, on a campaign that hasn't ended. */
  editable: boolean;
  /** A supporter, a gift of its own, or a campaign above or below it (the server's lock). */
  locked: boolean;
  /** The lock is the campaign above's alone: 11b's line names only what's below, so the server's says it. */
  lockedByAbove: boolean;
  /** A live campaign whose pages pick can't go back to picking for them (only a draft can). */
  draft: boolean;
  /** The names of this campaign's own charities. */
  ownCharities: readonly string[];
  /** The campaign above, when it picks the charities for everything under it (frame 11b). */
  bound: { parent: string } | null;
  onSaved: Saved;
}) {
  const module = useFundraisersModule();
  const live = useLivePage();
  const current = config.recipient_choice ?? 'campaign';
  const offered = config.recipient_choice_options ?? ['campaign'];
  const binding = config.binding_list ?? null;
  // The list that binds from above, with whose it is: picks must come from it (plan §FC-16.1).
  const fromAbove = binding && binding.campaign_id !== campaignId ? binding : null;
  const above = useRecipientNames(fromAbove);
  const ownList = useRecipientNames(config.recipient_list);
  const [choice, setChoice] = useState(current);
  const [list, setList] = useState<RecipientItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  useEffect(() => setChoice(current), [current]);
  // The list as edited here; it follows the saved list until the person changes it.
  const edited = list ?? ownList.items;
  const listChanged =
    list !== null &&
    (list.length !== ownList.items.length ||
      list.some((item, index) => key(item) !== key(ownList.items[index]!)));
  const save = useMutation({
    mutationFn: () => {
      const body: P2pConfigRequest = {
        recipient_choice: choice as P2pConfigRequest['recipient_choice'],
      };
      if (choice === 'supporters') {
        if (listChanged) body.recipient_list = edited.length > 0 ? idsOf(edited) : null;
      } else if (config.recipient_list) body.recipient_list = null;
      return sdk.campaignsUpdateCampaign({
        path: { fundraiser_id: campaignId },
        body,
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await onSaved();
      if (live()) {
        setList(null);
        setSearching(false);
        toast.success(C.choiceSaved);
      }
    },
    onError: (error) => saveFailed(live, error),
  });
  const names = (items: readonly string[]) => series(items);

  let body;
  if (bound) {
    body = (
      <p className="text-s14 leading-h15 text-text-strong" data-testid="campaign-who-bound">
        {C.whoBound(bound.parent, names(ownCharities))}
      </p>
    );
  } else if (offered.length === 1) {
    // One choice on offer (a charity's own campaign raises for itself, Q8): a statement.
    body = (
      <p className="text-s14 leading-h15 text-text-strong" data-testid="campaign-who-fixed">
        {C.whoFixed(campaignName, ownCharities.length ? names(ownCharities) : module.orgName)}
      </p>
    );
  } else if (locked || !editable) {
    body = (
      <div className="flex flex-col gap-12" data-testid="campaign-who-locked">
        {fromAbove && above.items.length > 0 && (
          <div className="flex flex-col gap-8">
            <p className="text-s13 leading-h15 text-text-secondary">
              {C.listFromAbove(fromAbove.campaign_name ?? C.anotherCampaign)}
            </p>
            <Chips items={above.items} testId="campaign-who-list-above" />
          </div>
        )}
        <div>
          <p className="text-s14 font-w600 text-text-strong">
            {current === 'supporters' ? C.whoSupporters : C.whoCampaign}
          </p>
          <p className="mt-2 text-s13 leading-h15 text-text-secondary">
            {current === 'supporters' ? C.whoSupportersDesc : C.whoCampaignDesc}
          </p>
        </div>
        {current === 'supporters' && ownList.items.length > 0 && (
          <Chips items={ownList.items} testId="campaign-who-list" />
        )}
        {locked && (
          <p className="flex items-center gap-6 text-s12 text-text-secondary">
            <Icon icon={Lock} size={12} aria-hidden />
            {lockedByAbove ? SERVER_WORDS.choiceLocked : C.whoLocked}
          </p>
        )}
      </div>
    );
  } else {
    const inList = new Set(edited.map(key));
    const fromAboveOffer = above.items.filter((item) => !inList.has(key(item)));
    body = (
      <div className="flex flex-col gap-16" data-testid="campaign-who-choice">
        {fromAbove && above.items.length > 0 && (
          <div className="flex flex-col gap-8">
            <p className="text-s13 leading-h15 text-text-secondary">
              {C.listFromAbove(fromAbove.campaign_name ?? C.anotherCampaign)}
            </p>
            <Chips items={above.items} testId="campaign-who-list-above" />
          </div>
        )}
        <RadioTileGroup
          value={choice}
          onValueChange={setChoice}
          disabled={save.isPending}
          aria-label={C.whoTitle}
          testId="campaign-who-radio"
        >
          <RadioTile
            value="campaign"
            label={C.whoCampaign}
            description={C.whoCampaignDesc}
            // A live campaign whose pages pick has no charities of its own to go back to.
            disabled={current === 'supporters' && !draft}
            testId="campaign-who-campaign"
          />
          <RadioTile
            value="supporters"
            label={C.whoSupporters}
            description={C.whoSupportersDesc}
            testId="campaign-who-supporters"
          />
        </RadioTileGroup>
        {choice === 'supporters' && (
          <div className="ml-[30px] flex flex-col gap-8" data-testid="campaign-who-list-editor">
            <p className="text-s13 font-w600 text-text-strong">{C.listLabel}</p>
            {edited.length > 0 && (
              <Chips
                items={edited}
                testId="campaign-who-list"
                onRemove={(item) => setList(edited.filter((other) => key(other) !== key(item)))}
              />
            )}
            <p className="text-s12 text-text-secondary">
              {fromAbove
                ? C.listHelpBound(fromAbove.campaign_name ?? C.anotherCampaign)
                : C.listHelp}
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              className="self-start"
              aria-expanded={searching}
              leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
              onClick={() => setSearching((now) => !now)}
              testId="campaign-who-list-add"
            >
              {C.addToList}
            </Button>
            {searching &&
              (fromAbove ? (
                // Under a list from above, a pick comes from that list.
                <PickerResults
                  loading={!above.ready}
                  error={false}
                  results={fromAboveOffer.map((item) => ({ ...item, subtitle: null }))}
                  addedIds={inList}
                  adding={save.isPending}
                  onAdd={(result) => setList([...edited, { ...result }])}
                  onRetry={() => undefined}
                  testIdPrefix="campaign-who-list"
                />
              ) : (
                <RecipientSearch
                  addedIds={inList}
                  adding={save.isPending}
                  onAdd={(result) => setList([...edited, { ...result }])}
                  testIdPrefix="campaign-who-list"
                />
              ))}
          </div>
        )}
        <p className="text-s12 text-text-secondary">{C.whoLocksHint}</p>
        <Button
          appearance="saas"
          tone="secondary"
          className="self-end"
          disabled={(choice === current && !listChanged) || save.isPending}
          loading={save.isPending}
          onClick={() => save.mutate()}
          testId="campaign-who-save"
        >
          {C.saveChoice}
        </Button>
      </div>
    );
  }
  return (
    <Card flush testId="campaign-who-card">
      <CardHeader title={C.whoTitle} />
      <CardBody>{body}</CardBody>
    </Card>
  );
}

// ── The campaign card's look ─────────────────────────────────────────────────────────────────────

type CardStyle = 'schedule' | 'leaderboard' | 'progress';

const STYLES: { value: CardStyle; label: string; description: string }[] = [
  { value: 'schedule', label: C.styleSchedule, description: C.styleScheduleDesc },
  { value: 'leaderboard', label: C.styleLeaderboard, description: C.styleLeaderboardDesc },
  { value: 'progress', label: C.styleProgress, description: C.styleProgressDesc },
];

function knownStyle(value: string | null | undefined): CardStyle | null {
  return value === 'schedule' || value === 'leaderboard' || value === 'progress' ? value : null;
}

/**
 * How this campaign looks wherever a page shows its card, set by the campaign shown (decision 3). Public
 * totals only: the preview reads the public campaign wire, so a campaign whose own switch is off shows no
 * amount and is never ranked (D-FCM-26). A draft counts nowhere publicly yet, so it previews as nothing
 * raised.
 */
export function CardStyleCard({
  campaignId,
  campaignName,
  config,
  editable,
  draft,
  goal,
  onSaved,
}: {
  campaignId: string;
  campaignName: string;
  config: P2pConfigOut;
  editable: boolean;
  draft: boolean;
  /** The campaign's own goal (a decimal money string), for a draft's preview. */
  goal: string | null;
  onSaved: Saved;
}) {
  const live = useLivePage();
  const current =
    knownStyle(config.card_style) ?? knownStyle(config.default_card_style) ?? 'progress';
  const [style, setStyle] = useState<CardStyle>(current);
  useEffect(() => setStyle(current), [current]);
  const save = useMutation({
    mutationFn: () =>
      sdk.campaignsUpdateCampaign({
        path: { fundraiser_id: campaignId },
        body: { card_style: style },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await onSaved();
      if (live()) toast.success('Campaign settings updated');
    },
    onError: (error) => saveFailed(live, error),
  });
  return (
    <Card flush testId="campaign-card-style-card">
      <CardHeader title={C.cardTitle} subtitle={C.cardStyleSubtitle} />
      <CardBody className="flex flex-col gap-16">
        <RadioTileGroup
          value={style}
          onValueChange={(value) => {
            const next = knownStyle(value);
            if (next) setStyle(next);
          }}
          disabled={!editable || save.isPending}
          aria-label={C.cardTitle}
          testId="campaign-card-style"
        >
          {STYLES.map((option) => (
            <RadioTile
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
              testId={`campaign-card-style-${option.value}`}
            />
          ))}
        </RadioTileGroup>
        <div className="flex flex-col gap-8" data-testid="campaign-card-preview">
          <p className="text-s12 font-w600 tracking-givmo text-text-secondary">{C.stylePreview}</p>
          <CardPreview
            style={style}
            campaignId={campaignId}
            campaignName={campaignName}
            endsAt={config.ends_at ?? null}
            draft={draft}
            goal={goal}
            plural={storedLayerName(config)?.plural ?? null}
          />
        </div>
        {editable && (
          <Button
            appearance="saas"
            tone="secondary"
            className="self-end"
            disabled={style === current || save.isPending}
            loading={save.isPending}
            onClick={() => save.mutate()}
            testId="campaign-card-style-save"
          >
            {C.styleSave}
          </Button>
        )}
      </CardBody>
    </Card>
  );
}

function CardPreview({
  style,
  campaignId,
  campaignName,
  endsAt,
  draft,
  goal,
  plural,
}: {
  style: CardStyle;
  campaignId: string;
  campaignName: string;
  endsAt: string | null;
  draft: boolean;
  goal: string | null;
  /** What this campaign calls the campaigns inside it, as typed (D-FCM-50), for "Show all". */
  plural: string | null;
}) {
  const campaign = useQuery({
    ...publicCampaignOptions(campaignId),
    enabled: !draft && (style === 'progress' || style === 'schedule'),
    retry: false,
  });
  const schedule = useQuery({
    ...scheduleOptions(campaignId),
    enabled: !draft && style === 'schedule',
    retry: false,
  });
  const boards = useQuery({
    ...publicBoardsOptions(campaignId),
    enabled: !draft && style === 'leaderboard',
    retry: false,
  });
  // Total and goal, as the public read serves them, and the bar from the goal percent it serves beside
  // them (FCM-BE99; FCM-WEB25's ruled frame 11): the module never divides money (`GoalThermometer`'s
  // law). The bar rides only beside a total and a goal, as the public card draws it (a hidden total
  // shows neither), and a draft, which counts nowhere publicly yet, previews $0.00 and an empty bar.
  const raised = draft ? '0.00' : (campaign.data?.raised_amount ?? null);
  const target = draft ? goal : (campaign.data?.goal_amount ?? null);
  const ofGoal = target ? C.styleRaisedOf(displayP2pMoney(target)) : null;
  const bar =
    raised !== null && ofGoal !== null
      ? goalPercentBarWidth(draft ? 0 : (campaign.data?.goal_progress_percent ?? null))
      : null;
  const total = (
    <>
      <p className="text-s13 text-text-secondary">
        {raised !== null && (
          <span className="text-s16 font-w700 tabular-nums text-text-strong">
            {displayP2pMoney(raised)}
          </span>
        )}{' '}
        {ofGoal ?? C.styleRaised}
      </p>
      {bar !== null && (
        <div
          className="mt-8 h-6 w-full overflow-hidden rounded-full bg-neutral-100"
          role="progressbar"
          aria-label={ofGoal ?? undefined}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={bar}
          data-testid="campaign-card-preview-bar"
        >
          <div className="h-full rounded-full bg-brand-500" style={{ width: `${bar}%` }} />
        </div>
      )}
    </>
  );
  let body;
  if (style === 'schedule') {
    // D-FCM-33 5.2 (RULED 2026-10-17): under the rows, the campaign's total against its goal, as the
    // public Schedule card has it (FCM-WEB26), so the preview and the card still match.
    body = (
      <>
        <ol className="flex flex-col divide-y divide-border-subtle">
          {(schedule.data?.rows ?? []).map((item) => (
            <li
              key={item.campaign_id}
              className="flex items-baseline gap-12 py-8 first:pt-0 last:pb-0"
            >
              <span className="w-[92px] shrink-0 text-s12 tabular-nums text-text-secondary">
                {item.ends_at ? formatEndDay(item.ends_at, item.ends_time_zone) : ''}
              </span>
              <span className="min-w-0 flex-1 text-s13 text-text-strong">
                {keepDayTogether(item.campaign_name)}
                {item.ended && (
                  <span className="ml-6 text-s12 text-text-secondary">{C.styleEnded}</span>
                )}
              </span>
              <span className="shrink-0 text-s13 font-w600 tabular-nums text-text-strong">
                {item.raised_amount == null ? '' : displayP2pMoney(item.raised_amount)}
              </span>
            </li>
          ))}
        </ol>
        {/* The card draws four games; the preview says there are more, and where they all are (the apps'
            "Show all" pill, D-FCM-49 (3); tester-5's item 10). */}
        {schedule.data &&
          schedule.data.total !== null &&
          schedule.data.total > schedule.data.rows.length && (
            <ModuleLink
              href={FUNDRAISERS_ROUTES.layers(campaignId)}
              testId="campaign-card-preview-show-all"
              className="mt-4 inline-flex min-h-44 items-center self-start text-s13 font-w600 text-brand-700 hover:underline lg:min-h-0"
            >
              {C.showAllCampaigns(schedule.data.total, plural)}
            </ModuleLink>
          )}
        <div
          className="mt-8 border-t border-border-subtle pt-8"
          data-testid="campaign-card-preview-total"
        >
          {total}
        </div>
      </>
    );
  } else if (style === 'leaderboard') {
    // Its top teams or pages: the campaigns under it when it has them, else its teams, else its pages.
    const data = boards.data;
    const leaders: { id: string; name: string; raised: string }[] = data
      ? (data.layers?.length ?? 0) > 0
        ? (data.layers ?? []).map((row) => ({
            id: row.campaign_id,
            name: row.campaign_name,
            raised: row.raised_amount,
          }))
        : data.teams.length > 0
          ? data.teams.map((row) => ({
              id: row.team_id,
              name: row.name,
              raised: row.raised_amount,
            }))
          : data.supporters.map((row) => ({
              id: row.supporter_id,
              name: row.display_name,
              raised: row.raised_amount,
            }))
      : [];
    const left = daysLeft(endsAt);
    body = (
      <div className="flex flex-col gap-8">
        <ol className="flex flex-col gap-6">
          {leaders.slice(0, 3).map((item, index) => (
            <li key={item.id} className="flex items-baseline gap-8 text-s13">
              <span className="w-12 shrink-0 tabular-nums text-text-secondary">{index + 1}</span>
              <span className="min-w-0 flex-1 text-text-strong">{item.name}</span>
              <span className="shrink-0 font-w600 tabular-nums text-text-strong">
                {displayP2pMoney(item.raised)}
              </span>
            </li>
          ))}
        </ol>
        {left !== null && <p className="text-s12 text-text-secondary">{C.styleDaysLeft(left)}</p>}
      </div>
    );
  } else {
    body = total;
  }
  return (
    <div
      className="max-w-[420px] rounded-lg border border-border-subtle bg-surface p-12"
      data-testid={`campaign-card-preview-${style}`}
    >
      <p className="mb-8 text-s13 font-w600 text-text-strong">{campaignName}</p>
      {body}
    </div>
  );
}
